import { MouseEventHandler, ReactNode, useState } from "react";
import {
  DefaultValues,
  FieldValues,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { FormattedMessage, MessageDescriptor } from "react-intl";
import { Link } from "react-router";
import {
  graphql,
  useMutation,
  useFragment,
  ConnectionHandler,
} from "react-relay";
import { toast } from "sonner";
import Button from "../Button";
import {
  BaseProjectVersionApprovalButtonFragment$key,
  BaseProjectVersionApprovalButtonFragment$data,
} from "./__generated__/BaseProjectVersionApprovalButtonFragment.graphql.ts";
import {
  BaseProjectVersionApprovalButtonCreateMutation,
  ProjectVersionApprovalInput as ProjectVersionApprovalCreateInput,
} from "./__generated__/BaseProjectVersionApprovalButtonCreateMutation.graphql.ts";
import {
  BaseProjectVersionApprovalButtonUpdateMutation,
  ProjectVersionApprovalInput as ProjectVersionApprovalUpdateInput,
} from "./__generated__/BaseProjectVersionApprovalButtonUpdateMutation.graphql.ts";
import { BaseProjectVersionApprovalButtonDeleteMutation } from "./__generated__/BaseProjectVersionApprovalButtonDeleteMutation.graphql.ts";
import { MdCreate } from "react-icons/md";
import {
  ResponsiveModal,
  ResponsiveModalCloseDrawer,
  ResponsiveModalContent,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from "@/kit/ui/responsive-modal.tsx";

const Fragment = graphql`
  fragment BaseProjectVersionApprovalButtonFragment on ProjectVersion {
    id
    version
    entity {
      displayName
      username
    }
    approval {
      __typename
      id
      accept
      scores {
        id
        value
        competitionScoringCriterion {
          id
        }
      }
      notes
    }
    project {
      id
      stage {
        id
        scoringCriteria {
          id
          title
          description
        }
      }
    }
    viewerCanApprove: viewerCan(action: CREATE_PROJECT_VERSION_APPROVAL)
    ...SubmissionStatusBadgeFragment
  }
`;

export type FragmentKey = BaseProjectVersionApprovalButtonFragment$key;
export type FragmentData = BaseProjectVersionApprovalButtonFragment$data;

const CreateMutation = graphql`
  mutation BaseProjectVersionApprovalButtonCreateMutation(
    $projectVersionId: ID!
    $input: ProjectVersionApprovalInput!
  ) {
    createProjectVersionApproval(
      projectVersionId: $projectVersionId
      input: $input
    ) {
      node {
        id
        projectVersion {
          ...BaseProjectVersionApprovalButtonFragment
          ...SubmissionApprovalsFragment
        }
      }
    }
  }
`;

const UpdateMutation = graphql`
  mutation BaseProjectVersionApprovalButtonUpdateMutation(
    $projectVersionApprovalId: ID!
    $input: ProjectVersionApprovalInput!
  ) {
    updateProjectVersionApproval(
      projectVersionApprovalId: $projectVersionApprovalId
      input: $input
    ) {
      node {
        id
        projectVersion {
          ...BaseProjectVersionApprovalButtonFragment
          ...SubmissionApprovalsFragment
        }
      }
    }
  }
`;

const DeleteMutation = graphql`
  mutation BaseProjectVersionApprovalButtonDeleteMutation(
    $projectVersionApprovalId: ID!
    $connections: [ID!]!
  ) {
    deleteProjectVersionApproval(
      projectVersionApprovalId: $projectVersionApprovalId
    ) @deleteEdge(connections: $connections)
  }
`;

export interface Messages {
  /// no values
  dialogTriggerCreate: MessageDescriptor;
  /// no values
  dialogTriggerUpdate: MessageDescriptor;
  /// values: {version, entity: displayName}
  dialogTitle: MessageDescriptor;
  /// no values
  createSubmit: MessageDescriptor;
  /// values: {entity: displayName}
  onCreateSuccess: MessageDescriptor;
  /// values: {entity: displayName}
  onCreateFailure: MessageDescriptor;
  /// no values
  updateSubmit: MessageDescriptor;
  /// values: {entity: displayName}
  onUpdateSuccess: MessageDescriptor;
  /// values: {entity: displayName}
  onUpdateFailure: MessageDescriptor;
  /// no values
  deleteSubmit: MessageDescriptor;
  /// values: {entity: displayName}
  onDeleteSuccess: MessageDescriptor;
  /// values: {entity: displayName}
  onDeleteFailure: MessageDescriptor;
}

interface BaseProps<FormData extends FieldValues> {
  fragment: FragmentKey;
  isHidden: (data: FragmentData) => boolean;
  getDefaultValues: (data: FragmentData) => DefaultValues<FormData>;
  getCreateInput: (data: FormData) => ProjectVersionApprovalCreateInput;
  getUpdateInput: (data: FormData) => ProjectVersionApprovalUpdateInput;
  messages: Messages;
  children: (
    form: UseFormReturn<FormData>,
    criteria: FragmentData["project"]["stage"]["scoringCriteria"],
  ) => ReactNode;
}

export const BaseProjectVersionApprovalButton = <FormData extends FieldValues>(
  props: BaseProps<FormData>,
) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const fragment = useFragment(Fragment, props.fragment);
  const { approval, entity, ...projectVersion } = fragment;

  const form = useForm<FormData>({
    defaultValues: props.getDefaultValues(fragment),
    shouldUseNativeValidation: true,
  });

  const [commitCreateMutation, isCreateMutationInFlight] =
    useMutation<BaseProjectVersionApprovalButtonCreateMutation>(CreateMutation);
  const [commitUpdateMutation, isUpdateMutationInFlight] =
    useMutation<BaseProjectVersionApprovalButtonUpdateMutation>(UpdateMutation);
  const [commitDeleteMutation, isDeleteMutationInFlight] =
    useMutation<BaseProjectVersionApprovalButtonDeleteMutation>(DeleteMutation);
  const isMutationInFlight =
    isCreateMutationInFlight ||
    isUpdateMutationInFlight ||
    isDeleteMutationInFlight;

  if (!fragment.viewerCanApprove || props.isHidden(fragment)) {
    return null;
  }

  const onConfirmApproval = form.handleSubmit((data) => {
    if (approval) {
      commitUpdateMutation({
        variables: {
          projectVersionApprovalId: approval.id,
          input: props.getUpdateInput(data),
        },
        onCompleted() {
          setDialogOpen(false);
          toast.success(
            <FormattedMessage
              {...props.messages.onUpdateSuccess}
              values={{ entity: entity.displayName }}
            />,
          );
        },
        onError(error) {
          console.error(error);
          toast.error(
            <FormattedMessage
              {...props.messages.onUpdateFailure}
              values={{ entity: entity.displayName }}
            />,
          );
        },
        updater(store) {
          store.get(fragment.project.stage.id)?.invalidateRecord();
        },
      });
    } else {
      commitCreateMutation({
        variables: {
          projectVersionId: projectVersion.id,
          input: props.getCreateInput(data),
        },
        onCompleted() {
          setDialogOpen(false);
          toast.success(
            <FormattedMessage
              {...props.messages.onCreateSuccess}
              values={{ entity: entity.displayName }}
            />,
          );
        },
        onError(error) {
          console.error(error);
          toast.error(
            <FormattedMessage
              {...props.messages.onCreateFailure}
              values={{ entity: entity.displayName }}
            />,
          );
        },
        updater(store) {
          store.get(fragment.project.stage.id)?.invalidateRecord();
        },
      });
    }
  });

  const onDeleteApproval: MouseEventHandler = () => {
    if (!approval) return;

    commitDeleteMutation({
      variables: {
        projectVersionApprovalId: approval.id,
        connections: [
          ConnectionHandler.getConnectionID(
            projectVersion.id,
            "SubmissionApprovalsFragment_approvals",
          ),
        ],
      },
      onCompleted() {
        setDialogOpen(false);
        toast.success(
          <FormattedMessage
            {...props.messages.onDeleteSuccess}
            values={{ entity: entity.displayName }}
          />,
        );
      },
      onError(error) {
        console.error(error);
        toast.error(
          <FormattedMessage
            {...props.messages.onDeleteFailure}
            values={{ entity: entity.displayName }}
          />,
        );
      },
      updater(store) {
        store.get(fragment.project.stage.id)?.invalidateRecord();
      },
    });
  };

  return (
    <ResponsiveModal open={dialogOpen} onOpenChange={setDialogOpen}>
      <ResponsiveModalTrigger asChild>
        <Button
          kind={approval ? "primary" : "secondary"}
          className="inline-flex flex-row items-center gap-1"
        >
          <FormattedMessage
            {...(approval
              ? props.messages.dialogTriggerUpdate
              : props.messages.dialogTriggerCreate)}
          />

          {approval && <MdCreate />}
        </Button>
      </ResponsiveModalTrigger>

      <ResponsiveModalContent className="xl:min-w-[920px] max-h-[90vh] overflow-y-auto">
        <div className="flex flex-row items-center px-4 md:py-2 py-4">
          <ResponsiveModalTitle className="flex-1">
            <FormattedMessage
              {...props.messages.dialogTitle}
              values={{
                version: projectVersion.version,
                entity: (
                  <Link to={`/${entity.username}`}>{entity.displayName}</Link>
                ),
              }}
            />
          </ResponsiveModalTitle>
        </div>

        <form
          onSubmit={onConfirmApproval}
          className="flex flex-col w-full px-4 md:py-2 py-4 gap-4"
        >
          {props.children(form, fragment.project.stage.scoringCriteria)}
          <Button
            className="w-full"
            type="submit"
            disabled={isMutationInFlight}
            onClick={onConfirmApproval}
          >
            {approval ? (
              <FormattedMessage {...props.messages.updateSubmit} />
            ) : (
              <FormattedMessage {...props.messages.createSubmit} />
            )}
          </Button>

          {approval && (
            <Button
              className="w-full"
              kind="danger"
              onClick={onDeleteApproval}
              disabled={isMutationInFlight}
            >
              <FormattedMessage {...props.messages.deleteSubmit} />
            </Button>
          )}

          <ResponsiveModalCloseDrawer className="mt-8 w-full" asChild>
            <Button kind="secondary" className="w-full">
              <FormattedMessage defaultMessage="Close" />
            </Button>
          </ResponsiveModalCloseDrawer>
        </form>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
};
